import * as React from "react";
import { forwardRef } from "react";

import { numFormat } from "@/shared.utils/number-format";
import { Box } from "@/shared-web.ui/ui.box";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Image as BaseImage } from "@/shared-web.ui/ui.image";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import {
  type MonetaryAmount,
  type Percentage,
  type ProductSkuPriceDetail,
} from "@/stf.data/schema";

type TitleProps = React.ComponentPropsWithoutRef<typeof Text>;
const Title = (props: TitleProps) => {
  return (
    <Text
      color="gray"
      highContrast
      lineClamp="1"
      size="3"
      weight="medium"
      {...props}
    />
  );
};

interface ImageProps
  extends Pick<
    React.ComponentPropsWithoutRef<typeof BaseImage>,
    "alt" | "src"
  > {
  className?: string;
}
const Image = (props: ImageProps) => {
  const { alt, className, src } = props;

  return (
    <Box
      className={className}
      flexShrink="0"
      height={{
        initial: "70px",
        xs: "100px",
      }}
      position="relative"
      width={{
        initial: "70px",
        xs: "100px",
      }}
    >
      <BaseImage
        alt={alt}
        className="rounded-4 shadow-2 bg-white-a12"
        responsive={false}
        transformation={[
          {
            width: 100,
            height: 100,
          },
        ]}
        src={src}
      />
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SeparatorImageWidthProps {}
const SeparatorImageWidth = forwardRef<HTMLSpanElement, SeparatorImageWidthProps>((props, ref) => {
  /**
   * Width of this separator should equal width of image specify above
   */
  return (
    <Separator
      ref={ref}
      className={`
        w-[70px]
        xs:w-[100px]
      `}
    />
  );
});
SeparatorImageWidth.displayName = "SeparatorImageWidth";

interface SelectedVariantProps {
  className?: string;
  selectedVariant: ProductSkuPriceDetail;
}
const SelectedVariant = (props: SelectedVariantProps) => {
  const { className, selectedVariant } = props;
  return (
    <Text as="div" className={className} color="gray" size="1">
      <ul>
        {selectedVariant.variantDisplay && Object.keys(selectedVariant.variantDisplay).length !== 0
          ? Object.entries(selectedVariant.variantDisplay).map(([label, value]) => {
            return (
              <li key={`${label}: ${value.optionName}`}>
                {`${label}: ${value.optionName}`}
              </li>
            );
          })
          : <li>{selectedVariant.variant}</li>}
      </ul>
    </Text>
  );
};

interface PriceProps {
  finalPrice: MonetaryAmount;
  finalPriceOrder?: "bottom" | "top";
  stickerPrice?: MonetaryAmount;
  weight?: "bold" | "medium";
}
const Price = (props: PriceProps) => {
  const {
    finalPrice,
    finalPriceOrder = "bottom",
    stickerPrice,
    weight = "medium",
  } = props;
  const isDiscount = stickerPrice ? finalPrice < stickerPrice : false;
  const isFinalNegative = finalPrice < 0;

  return (
    <Flex direction="column" align="end">
      <Flex
        className={cn(finalPriceOrder === "bottom" && "order-last")}
        direction="row"
      >
        <Text
          as="p"
          semanticColor={isDiscount || isFinalNegative ? "danger" : undefined}
        >
          <Text as="span" mr="1px" size="1" weight="light">
            ฿
          </Text>
          <Text as="span" size="3" weight={weight}>
            {numFormat(finalPrice, { decimal: 0 })}
          </Text>
        </Text>
      </Flex>
      {isDiscount && stickerPrice && (
        <Text
          as="p"
          className={cn(
            `
            text-[0.7rem] line-through decoration-gray-7
            `,
          )}
          color="gray"
          trim="both"
        >
          <Text as="span" className="text-[0.6rem]">
            ฿
          </Text>
          <Text as="span">{numFormat(stickerPrice, { decimal: 0 })}</Text>
        </Text>
      )}
    </Flex>
  );
};

interface PricePercentProps {
  discountPercentage: Percentage;
  finalPrice: MonetaryAmount;
  stickerPrice: MonetaryAmount;
  weight?: "bold" | "medium";
}
const PricePercent = (props: PricePercentProps) => {
  const {
    finalPrice,
    stickerPrice,
    discountPercentage,
    weight = "medium",
  } = props;
  const isDiscount = stickerPrice ? finalPrice < stickerPrice : false;
  const isFinalNegative = finalPrice < 0;

  return (
    <Flex
      direction="row"
      justify="center"
      align="center"
      gap="1"
    >
      <Flex
        direction="row"
      >
        <Text
          as="p"
          trim="start"
          semanticColor={isDiscount || isFinalNegative ? "danger" : undefined}
        >
          <Text as="span" mr="1px" size="1" weight="light">
            ฿
          </Text>
          <Text as="span" size="3" weight={weight}>
            {numFormat(finalPrice, { decimal: 0 })}
          </Text>
        </Text>
      </Flex>
      {discountPercentage
        ? (
            <Flex className={`
              p-px bg-danger-3 rounded-1 grow-0
            `}
            >
              <Text size="1" trim="start" semanticColor="danger">{`-${numFormat(discountPercentage, { decimal: 0 })}%`}</Text>
            </Flex>
          )
        : null}
    </Flex>
  );
};

interface QtyDisplayProps {
  qty: number;
  weight?: "bold" | "medium";
}
const QtyDisplay = (props: QtyDisplayProps) => {
  const { qty, weight = "medium" } = props;
  return (
    <Text as="div" color="gray">
      <Text as="span" className="mr-1" size="1">
        x
      </Text>
      <Text weight={weight}>{qty}</Text>
    </Text>
  );
};

export {
  Image,
  Price,
  PricePercent,
  QtyDisplay,
  SelectedVariant,
  SeparatorImageWidth,
  Title,
};
